import { useState } from 'react';
import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/base/Reveal';
import { accent } from '@/components/feature/solution/theme';

interface FlowStep {
  icon: string;
  title: string;
  desc: string;
}

interface ParkingFlowProps {
  data: {
    eyebrow: string;
    title: string;
    subtitle: string;
    steps: FlowStep[];
    systemNote: string;
  };
}

export default function ParkingFlow({ data }: ParkingFlowProps) {
  const a = accent('gold');
  const [active, setActive] = useState(0);

  return (
    <section id="flow" className="w-full px-4 md:px-6 py-16 md:py-24 bg-background-100 scroll-mt-20">
      <div className="max-w-[1200px] mx-auto">
        <SectionHeading eyebrow={data.eyebrow} title={data.title} subtitle={data.subtitle} />

        {/* 桌面端横向时间轴 */}
        <Reveal className="mt-14 hidden lg:block">
          <div className="relative">
            <div className="absolute top-9 left-[8.333%] right-[8.333%] h-px bg-background-300"></div>
            <div className="relative grid grid-cols-6 gap-3">
              {data.steps.map((step, idx) => {
                const isActive = idx === active;
                return (
                  <button
                    key={step.title}
                    type="button"
                    onClick={() => setActive(idx)}
                    className="flex flex-col items-center text-center cursor-pointer group"
                  >
                    <span
                      className={`relative z-10 w-[72px] h-[72px] flex items-center justify-center rounded-full border-2 transition-all duration-300 ${
                        isActive
                          ? `${a.solid} border-transparent text-foreground-950 scale-105`
                          : 'bg-background-50 border-background-300 text-secondary-600 group-hover:border-secondary-400'
                      }`}
                    >
                      <i className={`${step.icon} text-2xl`}></i>
                    </span>
                    <span className="mt-3 text-xs font-semibold text-foreground-400">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <span className="mt-1 font-heading font-bold text-foreground-950 text-sm">{step.title}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-8 rounded-2xl border border-background-200 bg-background-50 p-6">
            <div className="flex items-center gap-2 text-xs font-semibold text-secondary-700">
              <i className={`${data.steps[active].icon} text-base`}></i>
              步骤 {String(active + 1).padStart(2, '0')} · {data.steps[active].title}
            </div>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{data.steps[active].desc}</p>
          </div>
        </Reveal>

        {/* 移动端纵向时间轴 */}
        <div className="mt-10 lg:hidden flex flex-col gap-4">
          {data.steps.map((step, idx) => (
            <Reveal key={step.title} delay={idx * 60}>
              <div className="flex gap-4">
                <div className="flex flex-col items-center">
                  <span className={`w-12 h-12 flex items-center justify-center rounded-full border-2 ${a.strongBorder} ${a.text} shrink-0`}>
                    <i className={`${step.icon} text-xl`}></i>
                  </span>
                  {idx < data.steps.length - 1 && <span className={`w-px flex-1 mt-2 ${a.line}`}></span>}
                </div>
                <div className="pb-3">
                  <div className="text-xs font-semibold text-foreground-400">步骤 {String(idx + 1).padStart(2, '0')}</div>
                  <div className="mt-1 font-heading font-bold text-foreground-950">{step.title}</div>
                  <p className="mt-1 text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}